import React from 'react';
import PropTypes from 'prop-types';

import { Link, Outlet } from 'react-router-dom';

import {
  styled,
  Box,
  classNames,
  Portal,
  Stack,
  Typography,
  Divider,
  Tabs,
  Tab,
  AppBarSlotPortal,
} from '@datanomik/commons-ui';

import { useAbilityContext } from '@/contexts/ability';

import { useCreateUrl } from '@/hooks/useNavigate';

import { t } from 'i18next';
import { ArrowRightSLine } from '@datanomik/commons-ui/icons';
import { styles } from './styles';
import { useSidebarMenu } from './hooks';

const SidebarMenuRaw = (props) => {
  const { className, location } = props;

  const createUrl = useCreateUrl();
  const ability = useAbilityContext();

  const {
    routeItem,
    currentTab,
    isMobile,
    isMobileMd,
    showTabletTabs,
    sidebarMenuElement,
  } = useSidebarMenu({ location });

  const {
    route: { children: menuItems, label },
  } = routeItem;

  const classes = classNames({
    [className]: !!className,
  });

  return (
    <Box className={classes}>
      {sidebarMenuElement && !isMobileMd && (
        <Portal container={sidebarMenuElement}>
          <Stack
            sx={{
              width: '264px',
              height: '100%',
              padding: '20px',
              gap: '20px',
              flexShrink: 0,
              borderRight: '1px solid',
              borderColor: 'stroke.200',
              bgcolor: 'white',
            }}
          >
            <Stack gap="4px">
              <Typography variant="p3" fontWeight="500">
                {t(label)}
              </Typography>
              <Typography variant="p4" color="text.500">
                {t('Browse between categories')}
              </Typography>
            </Stack>
            <Divider />
            <Tabs orientation="vertical" value={currentTab} variant="standard">
              {
                menuItems.filter(({ showInMenu, key }) => showInMenu && ability.can('view', key)).map(({
                  path: childPath,
                  icon: childIcon,
                  label: childLabel,
                  key: childKey,
                  link: childLink,
                }) => (
                  <Tab
                    key={childKey}
                    orientation="vertical"
                    startAdornment={childIcon}
                    endAdornment={<ArrowRightSLine />}
                    value={createUrl(`/${location}/${childPath}`)}
                    {...({
                      component: childLink ? 'a' : Link,
                      target: childLink ? '_blank' : '',
                      ...(childLink
                        ? { href: childLink }
                        : { to: createUrl(`/${location}/${childPath}`) }),
                    })}
                  >
                    {t(childLabel)}
                  </Tab>
                ))
              }
            </Tabs>
          </Stack>
        </Portal>
      )}

      {isMobile && (
        <AppBarSlotPortal name="bottom-tabs-mobile">
          <Tabs orientation="horizontal" value={currentTab} variant="standard">
            {
              menuItems.filter(({ showInMenu, key }) => showInMenu && ability.can('view', key)).map(({
                path: childPath,
                icon: childIcon,
                label: childLabel,
                key: childKey,
                link: childLink,
              }) => (
                <Tab
                  key={childKey}
                  startAdornment={childIcon}
                  endAdornment={<ArrowRightSLine />}
                  value={createUrl(`/${location}/${childPath}`)}
                  {...({
                    component: childLink ? 'a' : Link,
                    target: childLink ? '_blank' : '',
                    ...(childLink
                      ? { href: childLink }
                      : { to: createUrl(`/${location}/${childPath}`) }),
                  })}
                >
                  {t(childLabel)}
                </Tab>
              ))
            }
          </Tabs>
        </AppBarSlotPortal>
      )}

      {showTabletTabs && (
        <AppBarSlotPortal name="bottom-tabs-desktop">
          <Tabs orientation="horizontal" value={currentTab} variant="standard">
            {
              menuItems.filter(({ showInMenu, key }) => showInMenu && ability.can('view', key)).map(({
                path: childPath,
                icon: childIcon,
                label: childLabel,
                key: childKey,
                link: childLink,
              }) => (
                <Tab
                  key={childKey}
                  startAdornment={childIcon}
                  endAdornment={<ArrowRightSLine />}
                  value={createUrl(`/${location}/${childPath}`)}
                  {...({
                    component: childLink ? 'a' : Link,
                    target: childLink ? '_blank' : '',
                    ...(childLink
                      ? { href: childLink }
                      : { to: createUrl(`/${location}/${childPath}`) }),
                  })}
                >
                  {t(childLabel)}
                </Tab>
              ))
            }
          </Tabs>
        </AppBarSlotPortal>
      )}

      <Outlet />
    </Box>
  );
};

SidebarMenuRaw.propTypes = {
  className: PropTypes.string,
  location: PropTypes.string,
};

export const SidebarMenuLayout = styled(SidebarMenuRaw)(styles);
